import { UiState } from '../state/UiState';
import { t } from '../i18n/i18n';
import { EditionSimulatorBar } from './EditionSimulatorBar';
import { HeaderBar, HeaderBarCallbacks } from './HeaderBar';
import { UpgradeModal } from './UpgradeModal';
import { ShareModal } from './ShareModal';
import { AdBanner } from './AdBanner';
import { LegalModal } from './LegalModal';
import { SiteGuideSection } from './SiteGuideSection';
import { LicenseService } from '../services/LicenseService';
import { subscribeLocaleChange } from '../i18n/i18n';

export interface AppShellOptions {
  uiState: UiState;
  headerCallbacks: Omit<HeaderBarCallbacks, 'onToggleControlPanel' | 'onUpgradeRequested'>;
}

export class AppShell {
  private rootElement: HTMLElement;
  private uiState: UiState;
  private simulatorBar: EditionSimulatorBar;
  private headerBar: HeaderBar;
  private upgradeModal: UpgradeModal;
  private shareModal: ShareModal;
  private legalModal: LegalModal;
  private adBanner: AdBanner;
  private siteGuideSection: SiteGuideSection;

  private appBodyEl: HTMLElement;
  private canvasAreaEl: HTMLElement;
  private controlPanelEl: HTMLElement;
  private mobileToggleBtn: HTMLButtonElement;
  private mobileBackdropEl: HTMLElement;
  private modalContainerEl: HTMLElement;

  constructor(options: AppShellOptions) {
    this.uiState = options.uiState;
    this.upgradeModal = new UpgradeModal(this.uiState);
    this.shareModal = new ShareModal();

    this.simulatorBar = new EditionSimulatorBar(this.uiState);
    this.headerBar = new HeaderBar(this.uiState, {
      ...options.headerCallbacks,
      onToggleControlPanel: () => {
        if (typeof window !== 'undefined' && window.innerWidth <= 768) {
          this.uiState.toggleMobilePanel();
        } else {
          this.uiState.togglePanel();
        }
      },
      onUpgradeRequested: (feature) => this.upgradeModal.show(feature),
    });

    this.adBanner = new AdBanner({
      uiState: this.uiState,
      onRemoveAdsRequested: () => this.upgradeModal.show('ad_free'),
    });
    this.siteGuideSection = new SiteGuideSection();

    this.rootElement = document.createElement('div');
    this.rootElement.className = 'app-root';
    this.rootElement.style.display = 'flex';
    this.rootElement.style.flexDirection = 'column';
    this.rootElement.style.minHeight = '100vh';

    this.appBodyEl = document.createElement('div');
    this.appBodyEl.className = 'app-body';

    this.canvasAreaEl = document.createElement('main');
    this.canvasAreaEl.className = 'canvas-area';
    this.canvasAreaEl.id = 'mainContainer';

    this.controlPanelEl = document.createElement('aside');
    this.controlPanelEl.className = 'control-panel';
    this.controlPanelEl.id = 'controlPanel';

    this.mobileToggleBtn = document.createElement('button');
    this.mobileToggleBtn.className = 'btn btn-primary mobile-panel-toggle';
    this.mobileToggleBtn.innerHTML = t().header.mobileOpen;
    this.mobileToggleBtn.addEventListener('click', () => {
      this.uiState.toggleMobilePanel();
    });

    this.mobileBackdropEl = document.createElement('div');
    this.mobileBackdropEl.className = 'mobile-panel-backdrop';
    this.mobileBackdropEl.addEventListener('click', () => {
      if (this.uiState.getSnapshot().isMobilePanelOpen) {
        this.uiState.toggleMobilePanel();
      }
    });

    this.modalContainerEl = document.createElement('div');
    this.modalContainerEl.id = 'modalContainer';

    this.legalModal = new LegalModal(this.modalContainerEl, this.uiState);

    this.render();

    this.uiState.subscribe((state) => {
      this.controlPanelEl.style.display = state.isPanelOpen ? 'flex' : 'none';
      this.controlPanelEl.classList.toggle('mobile-open', state.isMobilePanelOpen);
      this.mobileBackdropEl.classList.toggle('active', state.isMobilePanelOpen);
      const texts = t();
      this.mobileToggleBtn.innerHTML = state.isMobilePanelOpen ? texts.header.mobileClose : texts.header.mobileOpen;
    });

    // Pro版の場合は下部余白を0に、無料版の場合は広告分の余白48pxを確保
    LicenseService.getInstance().subscribe((isPro) => {
      this.appBodyEl.style.paddingBottom = isPro ? '0px' : '48px';
    });
  }

  getRootElement(): HTMLElement {
    return this.rootElement;
  }

  getCanvasArea(): HTMLElement {
    return this.canvasAreaEl;
  }

  getControlPanel(): HTMLElement {
    return this.controlPanelEl;
  }

  getModalContainer(): HTMLElement {
    return this.modalContainerEl;
  }

  getUpgradeModal(): UpgradeModal {
    return this.upgradeModal;
  }

  getShareModal(): ShareModal {
    return this.shareModal;
  }

  getLegalModal(): LegalModal {
    return this.legalModal;
  }

  getAdBanner(): AdBanner {
    return this.adBanner;
  }

  private render(): void {
    const showSimulator = typeof window !== 'undefined' && (
      window.location.search.includes('simulator=1') ||
      window.location.search.includes('debug=1')
    );
    if (showSimulator) {
      this.rootElement.appendChild(this.simulatorBar.getElement());
    }
    this.rootElement.appendChild(this.headerBar.getElement());

    this.appBodyEl.appendChild(this.canvasAreaEl);
    this.appBodyEl.appendChild(this.mobileBackdropEl);
    this.appBodyEl.appendChild(this.controlPanelEl);
    this.appBodyEl.appendChild(this.mobileToggleBtn);
    this.rootElement.appendChild(this.appBodyEl);

    // 下部詳細解説＆FAQセクション (AdSense審査・SEO対策)
    this.rootElement.appendChild(this.siteGuideSection.getElement());

    // 下部広告バナー
    this.rootElement.appendChild(this.adBanner.getElement());

    this.modalContainerEl.appendChild(this.upgradeModal.getElement());
    this.modalContainerEl.appendChild(this.shareModal.getElement());
    this.rootElement.appendChild(this.modalContainerEl);
  }
}
